import * as d3 from 'd3'
import { motion } from 'motion/react'
import * as React from 'react'

import { LabeledSwatch } from '@/components/ui/LabeledSwatch'
import { cn } from '@/utils'
import { ChartGrid } from './Grid'
import { useChartBase } from './Hooks'
import { ChartLegend } from './Legend'
import { ChartTooltip } from './Tooltip'
import type { ChartExportProps, MultiSeriesDataPoint } from './types'

interface SeriesConfig {
  key: string
  label: string
  color: string
}

function roundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  topRadius: number,
  bottomRadius: number,
): string {
  const safeHeight = Math.max(height, 0)
  const safeWidth = Math.max(width, 0)
  const tr = Math.min(topRadius, safeHeight / 2, safeWidth / 2)
  const br = Math.min(bottomRadius, safeHeight / 2, safeWidth / 2)

  return [
    `M${x + tr},${y}`,
    `h${safeWidth - 2 * tr}`,
    `a${tr},${tr} 0 0 1 ${tr},${tr}`,
    `v${safeHeight - tr - br}`,
    `a${br},${br} 0 0 1 ${-br},${br}`,
    `h${-(safeWidth - 2 * br)}`,
    `a${br},${br} 0 0 1 ${-br},${-br}`,
    `v${-(safeHeight - tr - br)}`,
    `a${tr},${tr} 0 0 1 ${tr},${-tr}`,
    'Z',
  ].join('')
}

interface StackedBarChartProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ChartExportProps {
  data?: MultiSeriesDataPoint[]
  series?: SeriesConfig[]
  className?: string
  height?: number
  marginTop?: number
  marginRight?: number
  marginBottom?: number
  marginLeft?: number
  radius?: number
  onBarClick?: (data: MultiSeriesDataPoint) => void
  rotateXLabels?: boolean
  truncateXLabels?: boolean
  maxLabelLength?: number
  tickSpacing?: number
  onChartReady?: (params: {
    containerRef: React.RefObject<HTMLDivElement | null>
    isFullscreen: boolean
    toggleFullscreen: () => Promise<void>
  }) => void
  yTickFormatter?: (value: any) => string
  showLegend?: boolean
  legendPosition?: 'right' | 'bottom' | 'top'
}

const StackedBarChart = React.forwardRef<HTMLDivElement, StackedBarChartProps>(
  (
    {
      data = [],
      series = [],
      className,
      height = 400,
      marginTop = 20,
      marginRight = 30,
      marginBottom = 30,
      marginLeft = 40,
      radius = 4,
      onBarClick,
      rotateXLabels = false,
      truncateXLabels = false,
      maxLabelLength = 12,
      tickSpacing,
      onChartReady,
      yTickFormatter,
      showLegend = false,
      legendPosition = 'bottom',
      ...props
    },
    ref,
  ) => {
    const {
      containerRef,
      dimensions,
      isFullscreen,
      hoveredData,
      setHoveredData,
    } = useChartBase(onChartReady)

    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement)

    const adjustedMarginRight = React.useMemo(() => {
      return showLegend && legendPosition === 'right'
        ? Math.max(marginRight, 220)
        : marginRight
    }, [marginRight, showLegend, legendPosition])

    const adjustedMarginTop = React.useMemo(() => {
      return showLegend && legendPosition === 'top'
        ? Math.max(marginTop, marginTop + 40)
        : marginTop
    }, [marginTop, showLegend, legendPosition])

    const adjustedMarginBottom = React.useMemo(() => {
      return showLegend && legendPosition === 'bottom'
        ? Math.max(marginBottom, marginBottom + 40)
        : marginBottom
    }, [marginBottom, showLegend, legendPosition])

    const sortedSeries = React.useMemo(() => {
      return [...series].sort((first, second) => {
        const firstTotal = data.reduce((sum, dataPoint) => {
          const value = Number(dataPoint[first.key])
          return sum + (Number.isNaN(value) ? 0 : value)
        }, 0)
        const secondTotal = data.reduce((sum, dataPoint) => {
          const value = Number(dataPoint[second.key])
          return sum + (Number.isNaN(value) ? 0 : value)
        }, 0)
        return secondTotal - firstTotal
      })
    }, [series, data])

    const { xScale, yScale, stackedLayers } = React.useMemo(() => {
      if (
        !dimensions.width ||
        !dimensions.height ||
        !data.length ||
        !sortedSeries.length
      )
        return {}

      const bandScale = d3
        .scaleBand()
        .domain(data.map((dataPoint) => dataPoint.date))
        .range([marginLeft, dimensions.width - adjustedMarginRight])
        .padding(0.3)

      const seriesKeys = sortedSeries.map((seriesItem) => seriesItem.key)
      const stack = d3.stack<MultiSeriesDataPoint>().keys(seriesKeys)
      const layers = stack(data)

      const maxStackedValue =
        d3.max(layers, (layer) => d3.max(layer, (segment) => segment[1])) || 0

      const linearScale = d3
        .scaleLinear()
        .domain([0, maxStackedValue])
        .nice()
        .range([dimensions.height - adjustedMarginBottom, adjustedMarginTop])

      if (linearScale.domain()[0] !== 0) {
        linearScale.domain([0, linearScale.domain()[1]])
      }

      return {
        xScale: bandScale,
        yScale: linearScale,
        stackedLayers: layers,
      }
    }, [
      data,
      sortedSeries,
      dimensions,
      adjustedMarginTop,
      adjustedMarginRight,
      adjustedMarginBottom,
      marginLeft,
    ])

    const handlePointerEnter = (dataPoint: MultiSeriesDataPoint) => {
      setHoveredData(dataPoint)
    }

    const handlePointerLeave = () => {
      setHoveredData(null)
    }

    if (!data.length || !series.length) return null

    return (
      <div
        ref={containerRef}
        className={cn(
          'w-full h-full relative group',
          isFullscreen && 'bg-white',
          className,
        )}
        {...props}
      >
        {dimensions.width > 0 &&
          dimensions.height > 0 &&
          xScale &&
          yScale &&
          stackedLayers && (
            <svg
              role="img"
              aria-label="Stacked bar chart"
              width={dimensions.width}
              height={dimensions.height}
              viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
              className="overflow-visible"
            >
              <ChartGrid
                dimensions={dimensions}
                xScale={xScale}
                yScale={yScale}
                margin={{
                  top: adjustedMarginTop,
                  right: adjustedMarginRight,
                  bottom: adjustedMarginBottom,
                  left: marginLeft,
                }}
                yTickFormatter={yTickFormatter}
                fontSize={isFullscreen ? 14 : 10}
                rotateXLabels={rotateXLabels}
                truncateXLabels={truncateXLabels}
                maxLabelLength={maxLabelLength}
                tickSpacing={tickSpacing}
              />

              <g>
                {stackedLayers.map((layer, layerIndex) => {
                  const seriesItem = sortedSeries[layerIndex]
                  const isBottomLayer = layerIndex === 0
                  const isTopLayer = layerIndex === stackedLayers.length - 1
                  return (
                    <g key={seriesItem.key}>
                      {layer.map((segment, segmentIndex) => {
                        const [bottom, top] = segment
                        const MIN_BAR_HEIGHT = 4
                        const hasValue = top - bottom > 0
                        const rawHeight = Math.abs(yScale(bottom) - yScale(top))
                        const barHeight =
                          hasValue && rawHeight < MIN_BAR_HEIGHT
                            ? MIN_BAR_HEIGHT
                            : rawHeight
                        const barY = yScale(bottom) - barHeight
                        const barX = xScale(data[segmentIndex].date) ?? 0
                        const barWidth = xScale.bandwidth()

                        const hasValueAbove =
                          !isTopLayer &&
                          stackedLayers
                            .slice(layerIndex + 1)
                            .some((upperLayer) => {
                              const [upperBottom, upperTop] =
                                upperLayer[segmentIndex]
                              return upperTop - upperBottom > 0
                            })
                        const hasValueBelow =
                          !isBottomLayer &&
                          stackedLayers
                            .slice(0, layerIndex)
                            .some((lowerLayer) => {
                              const [lowerBottom, lowerTop] =
                                lowerLayer[segmentIndex]
                              return lowerTop - lowerBottom > 0
                            })

                        const topRounding = hasValueAbove ? 0 : radius
                        const bottomRounding = hasValueBelow ? 0 : radius

                        const animatedPath = roundedRectPath(
                          barX,
                          barY,
                          barWidth,
                          barHeight,
                          topRounding,
                          bottomRounding,
                        )
                        const initialPath = roundedRectPath(
                          barX,
                          yScale(0),
                          barWidth,
                          0,
                          topRounding,
                          bottomRounding,
                        )

                        return (
                          <motion.path
                            key={data[segmentIndex].date}
                            fill={seriesItem.color}
                            initial={{ d: initialPath }}
                            animate={{ d: animatedPath }}
                            transition={{
                              duration: 0.6,
                              ease: 'easeOut',
                              delay: segmentIndex * 0.025 + layerIndex * 0.1,
                            }}
                            onPointerEnter={() =>
                              handlePointerEnter(data[segmentIndex])
                            }
                            onPointerLeave={handlePointerLeave}
                            onClick={() => onBarClick?.(data[segmentIndex])}
                            className="opacity-90 hover:opacity-100 transition-opacity cursor-pointer"
                          />
                        )
                      })}
                    </g>
                  )
                })}
              </g>
            </svg>
          )}

        {showLegend && (
          <ChartLegend
            items={sortedSeries.map((seriesItem) => ({
              key: seriesItem.key,
              label: seriesItem.label,
              color: seriesItem.color,
            }))}
            position={legendPosition}
            interactive={false}
            chartMargins={{
              top: adjustedMarginTop,
              bottom: adjustedMarginBottom,
            }}
          />
        )}

        <ChartTooltip
          active={!!hoveredData}
          position={
            hoveredData && xScale && yScale
              ? {
                  x: (xScale(hoveredData.date) ?? 0) + xScale.bandwidth() / 2,
                  y: yScale(
                    sortedSeries.reduce((sum, seriesItem) => {
                      const value = Number(hoveredData[seriesItem.key])
                      return sum + (Number.isNaN(value) ? 0 : value)
                    }, 0),
                  ),
                }
              : undefined
          }
        >
          {hoveredData && (
            <div className="space-y-1">
              <div className="font-semibold text-xs">{hoveredData.date}</div>
              {sortedSeries
                .filter((seriesItem) => {
                  const value = Number(hoveredData[seriesItem.key])
                  return !Number.isNaN(value) && value > 0
                })
                .map((seriesItem) => {
                  const value = Number(hoveredData[seriesItem.key])
                  return (
                    <LabeledSwatch
                      key={seriesItem.key}
                      color={seriesItem.color}
                      className="text-xs"
                      label={
                        <span className="text-muted-foreground">
                          {seriesItem.label}:
                        </span>
                      }
                      value={
                        <span className="font-medium">
                          $
                          {value.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      }
                    />
                  )
                })}
            </div>
          )}
        </ChartTooltip>
      </div>
    )
  },
)
StackedBarChart.displayName = 'StackedBarChart'

export { StackedBarChart }
export type { SeriesConfig, StackedBarChartProps }
